import { Modal } from "antd"
import { useEffect, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ArrowIcon } from "../../../components/SvgComponents/ArrowIcon/ArrowIcon";
import { CancelIcon } from "../../../components/SvgComponents/CancelIcon/CancelIcon";
import { DownloadAttachmentIcon } from "../../SvgComponents/DownloadAttachmentIcon/DownloadAttachmentIcon";

import "./AttachmentRenderModal.css";
import SignatureService from "../../../services/SignatureService";
import DownloadDocumentService from "../../../services/DownloadDocumentService";
import Helper from "../../../util/helper";
import { Loader } from "../../Loader/Loader";

export const AttachmentRenderModal = ({
  attachmentInfo,
  setIsAttachmentModalOpen,
  hasNextAttachment,
  handleContinueClick,
  signerGuid,
  allowDownloadDocument
}) => {
  const [loadingRendering, setLoadingRendering] = useState(false);
  const [loadingMorePages, setLoadingMorePages] = useState(false);
  const [attachmentRenderedImages, setAttachmentRenderedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [nextPage, setNextPage] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isVertical, setIsVertical] = useState(false);
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  const modalWidth = isDesktop ? "50%" : "100%";

  const detectDocumentOrientation = (images) => {
    if (!images || images.length === 0) return;
    const img = new Image();

    setIsVertical(img.height > img.width);
  };

  const getAttachmentRender = () => {
    if (attachmentInfo) {
      setLoadingRendering(true);
      SignatureService.getAttachmentRenderPage(signerGuid, attachmentInfo.GUID, 1)
        .then((response) => {
          setAttachmentRenderedImages(response.data.DocPageRendered);
          setNumberOfPages(response.data.NumberOfPages);
          setNextPage(response.data.NextPage);
          setCurrentPage(1);
          setScrollPosition(0);
          setLoadingRendering(false);

          // Detect document orientation after images are loaded
          detectDocumentOrientation(response.data.DocPageRendered);

          const scrollContainer = document.getElementById("attachment-images-container");
          if (scrollContainer) {
            scrollContainer.scrollTop = 0;
          }
        })
        .catch((error) => {
          console.error("Error fetching attachment render:", error);
          setLoadingRendering(false);
        });
    }
  }

  const loadMorePages = () => {
    if (loadingMorePages || nextPage === -1 || !attachmentInfo) {
      return;
    }

    setLoadingMorePages(true);
    SignatureService.getAttachmentRenderPage(signerGuid, attachmentInfo.GUID, nextPage)
      .then((response) => {
        setAttachmentRenderedImages(prevImages => [...prevImages, ...response.data.DocPageRendered]);
        setNextPage(response.data.NextPage);
        setLoadingMorePages(false);
        setTimeout(() => {
          const scrollContainer = document.getElementById("attachment-images-container");
          if (scrollContainer && scrollPosition > 0) {
            scrollContainer.scrollTop = scrollPosition;
          }
        }, 100);
      })
      .catch((error) => {
        console.error("Error fetching more attachment pages:", error);
        setLoadingMorePages(false);
      });
  }

  useEffect(() => {
    getAttachmentRender();
  }, [attachmentInfo]);

  const totalPages = numberOfPages;

  const handleScroll = (e) => {
    // Calculate the scroll position based on rendered pages (loaded pages)
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;
    const clientHeight = e.target.clientHeight;
    const renderedPages = attachmentRenderedImages.length;

    setScrollPosition(scrollTop);

    if (renderedPages === 0) {
      setCurrentPage(1);
      return;
    }

    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0 || renderedPages <= 0) {
      setCurrentPage(1);
      return;
    }

    const pageHeight = maxScroll / renderedPages;
    const currentPositionPage = Math.floor(scrollTop / pageHeight) + 1;

    const boundedPage = Math.max(1, Math.min(currentPositionPage, renderedPages));

    // Only update if we have a valid page number
    if (!isNaN(boundedPage) && boundedPage > 0) {
      setCurrentPage(boundedPage);
    }

    // Check if user reached the end of the penultimate page and load more pages
    if (boundedPage === renderedPages && renderedPages > 1) {
      loadMorePages();
    }
  }

  const createDownloadElementAndClick = (fileURL, fileName) => {
    var anchorElement = window.document.createElement('a');
    anchorElement.href = fileURL;
    anchorElement.target = '_self';
    anchorElement.download = fileName;
    var event = window.document.createEvent("MouseEvents");
    event.initEvent("click", true, false);
    anchorElement.dispatchEvent(event);
  };

  const handlerDownloadAttachment = () => {
    setDownloading(true);
    DownloadDocumentService.downloadDocumentAttachment(signerGuid, attachmentInfo.GUID)
      .then(response => {
        const fileName = attachmentInfo.FileName + '.pdf';
        const docContent = response.data.DocContent;
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          var byteCharacters = atob(docContent);
          var byteNumbers = new Array(byteCharacters.length);
          for (var i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          var byteArray = new Uint8Array(byteNumbers);
          var blob = new Blob([byteArray], { type: 'application/pdf' });
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          var newBlob = new Blob([Helper.base64ToArrayBuffer(docContent)], { type: 'application/octet-stream' });
          var fileURL = window.URL.createObjectURL(newBlob);
          createDownloadElementAndClick(fileURL, fileName);
        }
      })
      .catch(err => {
        console.error("Error downloading attachment:", err);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const truncateFileName = (fileName) => {
    if (!fileName) return '';
    const maxLength = isMobile ? 20 : 40;
    return fileName.length > maxLength ? `${fileName.slice(0, maxLength)}...` : fileName;
  };

  const titleContent = <div className="attachment-title-container">
    <span>{truncateFileName(attachmentInfo?.FileName)}</span>
    {!loadingRendering && <span>{currentPage}/{totalPages}</span>}
  </div>;

  return (
    <>
      <Modal
        title={titleContent}
        width={modalWidth}
        footer={null}
        open={!!attachmentInfo}
        className="attachment-images-modal"
        style={isDesktop ? {
          top: '5vh',
          height: '90vh',
        } : {
          top: 0,
          left: 0,
          position: 'absolute',
          minWidth: '100vw',
          minHeight: '100vh'
        }}
        closable={true}
        onCancel={() => setIsAttachmentModalOpen(false)}
      >
        <hr />
        {allowDownloadDocument && (
          <div className="attachment-download-wrapper">
            <button disabled={downloading} onClick={handlerDownloadAttachment} className="attachment-download-btn" title={t('general.download')}>
              <span>{t('general.download')}</span>
              <DownloadAttachmentIcon />
            </button>
          </div>
        )}
        <div
          className={`modal-container attachment-images-container ${!isVertical ? 'orientation-landscape' : ''}`}
          id="attachment-images-container"
          onScroll={handleScroll}
        >
          {
            (loadingRendering || loadingMorePages) ? <Loader /> :
              attachmentRenderedImages.map((attachmentData, index) => (
                <img
                  key={index}
                  className={`attachment-img ${!isVertical ? 'img-landscape' : ''}`}
                  src={`data:image/jpeg;base64,${attachmentData}`}
                  alt={`Page ${index + 1}`} />
              ))
          }
        </div>
        {isDesktop && <hr />}
        <div className="modal-buttons-container attachment-buttons-container">
          {isDesktop && <button
            onClick={() => setIsAttachmentModalOpen(false)}
            className={"attached-button"}
          >
            <span>{t('general.close')}</span>
            <CancelIcon />
          </button>}
          <button
            onClick={handleContinueClick}
            className={"attached-button continue-attachment-button"}
          >
            <span>{hasNextAttachment ? t("attachments.next_file") : t('general.continue')}</span>
            <ArrowIcon />
          </button>
        </div>
      </Modal>
    </>
  );
};
